<script setup lang="ts">
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { Video } from 'lucide-vue-next'

/**
 * The week strip that both dashboards show.
 *
 * `counterpart_name` is whoever is on the other side of the lesson: the tutor
 * when a student is looking, the student when a tutor is. The component does not
 * care which — it just labels the block with the person you are meeting.
 */
interface Lesson {
  id: number
  schedule_id?: number
  title: string
  counterpart_name?: string
  subject: string
  date: string
  time: string
  raw_time?: string
  type: 'one-on-one' | 'group'
  status?: string
  duration_minutes?: number
  lesson_links?: {
    room_id: string
    teacher_link: string
    student_link: string
    generated_at: string
  } | null
}

const props = withDefaults(
  defineProps<{
    lessons: Lesson[]
    calendarUrl?: string
    lessonBaseUrl?: string
  }>(),
  {
    calendarUrl: '/student/schedule',
    lessonBaseUrl: '/student/lessons',
  },
)

const emit = defineEmits<{
  lessonClick: [lesson: Lesson]
}>()

function isSessionActive(lesson: Lesson): boolean {
  return !!lesson.lesson_links && lesson.status === 'ongoing'
}

function getLessonPageUrl(lesson: Lesson): string {
  const scheduleId = lesson.schedule_id || lesson.id
  return `${props.lessonBaseUrl}/${scheduleId}/${lesson.date}`
}

// Get the current week's dates starting from Monday
const weekDays = computed(() => {
  const today = new Date()
  const currentDay = today.getDay()
  // Adjust to get Monday (day 1) as start of week
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay
  const monday = new Date(today)
  monday.setDate(today.getDate() + mondayOffset)

  const days = []
  const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

  for (let i = 0; i < 7; i++) {
    const date = new Date(monday)
    date.setDate(monday.getDate() + i)
    days.push({
      name: dayNames[i],
      date: date.getDate(),
      dateStr: date.toISOString().split('T')[0],
      isToday: date.toDateString() === today.toDateString()
    })
  }

  return days
})

function getLessonsForDate(dateStr: string): Lesson[] {
  return props.lessons.filter(lesson => lesson.date === dateStr).sort((a, b) => (a.time || '').localeCompare(b.time || ''))
}

function getTypeColor(type: string): { bg: string; border: string; badge: string } {
  if (type === 'one-on-one') {
    return {
      bg: 'bg-blue-100',
      border: 'border-blue-600',
      badge: 'bg-blue-600'
    }
  }
  return {
    bg: 'bg-purple-100',
    border: 'border-purple-600',
    badge: 'bg-purple-600'
  }
}

function getTypeLabel(type: string): string {
  return type === 'one-on-one' ? '1:1' : 'Group'
}
</script>

<template>
  <section>
    <div class="flex items-center justify-between mb-3">
      <h2 class="text-lg font-bold text-gray-900">Schedule</h2>
      <Link :href="calendarUrl" class="text-blue-600 font-medium text-xs hover:underline">View Full Calendar</Link>
    </div>
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <!-- Week Days Header -->
      <div class="grid grid-cols-7 border-b border-gray-200">
        <div
          v-for="(day, index) in weekDays"
          :key="day.dateStr"
          :class="[
            'p-2 text-center',
            index < 6 ? 'border-r border-gray-200' : '',
            day.isToday ? 'bg-blue-50' : ''
          ]"
        >
          <p :class="['text-xs font-semibold mb-1', day.isToday ? 'text-blue-600' : 'text-gray-500']">{{ day.name }}</p>
          <p :class="['text-sm font-bold', day.isToday ? 'text-blue-600' : 'text-gray-900']">{{ day.date }}</p>
        </div>
      </div>

      <!-- Lessons Grid -->
      <div class="grid grid-cols-7">
        <div
          v-for="(day, index) in weekDays"
          :key="`lessons-${day.dateStr}`"
          :class="[
            'p-2 min-h-[80px]',
            index < 6 ? 'border-r border-gray-200' : '',
            day.isToday ? 'bg-blue-50' : ''
          ]"
        >
          <div
            v-for="lesson in getLessonsForDate(day.dateStr)"
            :key="lesson.id"
            :class="[
              'rounded px-2 py-1 mb-1 border-l-2 cursor-pointer hover:shadow-md transition-shadow',
              getTypeColor(lesson.type).bg,
              getTypeColor(lesson.type).border
            ]"
            @click="emit('lessonClick', lesson)"
          >
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-gray-900 truncate">
                {{ lesson.subject }}<template v-if="lesson.counterpart_name"> - {{ lesson.counterpart_name.split(' ')[0] }}</template>
              </span>
              <span :class="['text-xs text-white px-1 py-0.5 rounded font-semibold ml-1 shrink-0', getTypeColor(lesson.type).badge]">
                {{ getTypeLabel(lesson.type) }}
              </span>
            </div>
            <p class="text-xs text-gray-700">{{ lesson.time }}</p>
            <Link
              v-if="isSessionActive(lesson)"
              :href="getLessonPageUrl(lesson)"
              class="mt-1 flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 hover:bg-green-200 rounded px-1.5 py-0.5 transition-colors w-fit"
              @click.stop
            >
              <Video class="w-3 h-3" />
              Join
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
